import { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TablePaginationConfig } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { CommonTable } from '@/shared';
import { useRubberProductColumns } from '../model/columns';
import { RubberCatalogResponse } from '@/shared/state/api/swagger';
import { RubberProductTableProps } from '../model/model';

export const RubberProductTable: FC<RubberProductTableProps> = ({ rubbers, onEdit, onCreateSimilar, onDelete }) => {
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = Number(searchParams.get('currentPage') || 1);
	const pageSize = Number(searchParams.get('pageSize') || 32);
	const searchString = searchParams.get('stringToSearch') || '';

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<RubberCatalogResponse> | SorterResult<RubberCatalogResponse>[],
	) => {
		const sortedBy = Array.isArray(sorter) ? sorter[0].column?.key?.toString() : sorter.column?.key?.toString();
		const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
		const preparedSortDir = sortOrder ? (sortOrder === 'ascend' ? 'asc' : 'desc') : undefined;

		const newParams: Record<string, string> = {};
		if (currentPage !== 1) newParams.currentPage = String(currentPage);
		if (pageSize !== 32) newParams.pageSize = String(pageSize);
		if (searchString) newParams.stringToSearch = searchString;
		if (sortedBy) newParams.orderBy = sortedBy;
		if (preparedSortDir) newParams.orderDir = preparedSortDir;

		setSearchParams(newParams);
	};

	const handlePageChange = (page: number, pageSize: number) => {
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev);
			newParams.set('currentPage', page.toString());
			newParams.set('pageSize', pageSize.toString());
			return newParams;
		});
	};

	const handleDelete = (row: RubberCatalogResponse) => {
		onDelete?.(row);
	};

	const handleEdit = (row: RubberCatalogResponse) => {
		onEdit?.(row);
	};

	const handleCreateSimilar = (row: RubberCatalogResponse) => {
		onCreateSimilar?.(row);
	};

	const RUBBER_PRODUCT_CATALOG_COLUMNS = useRubberProductColumns(handleDelete, handleEdit, handleCreateSimilar);

	return (
		<CommonTable
			columns={RUBBER_PRODUCT_CATALOG_COLUMNS}
			data={rubbers}
			onChange={handleTableChange}
			pageSize={pageSize}
			total={rubbers.length}
			page={currentPage}
			onPageChange={handlePageChange}
		/>
	);
};
